import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  inject,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { FileUploadModule } from 'primeng/fileupload';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { utils } from '@app/shared';
import { TicketService } from '../../../core/services/ticket.service';
import { TicketDetail, TicketLog } from '../../../core/models/ticket.model';
import { TimeEntryModalComponent } from '../time-entry-modal/time-entry-modal.component';

export type ActionType = 'comment' | 'attachment' | 'timeentry';

@Component({
  selector: 'app-ticket-action-bar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TextareaModule,
    ButtonModule,
    FileUploadModule,
    ToggleButtonModule,
    ToggleSwitchModule,
    DialogModule,
    InputTextModule,
    TooltipModule,
    TimeEntryModalComponent,
  ],
  templateUrl: './ticket-action-bar.component.html',
  styleUrls: ['./ticket-action-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketActionBarComponent {
  private readonly ticketService = inject(TicketService);
  private readonly messageService = inject(MessageService);

  @Input({ required: true }) set ticket(value: TicketDetail) {
    this._ticket = value;
    // Atualizar apontamento ativo quando o ticket mudar
    this.updateActiveTimeEntry();
  }

  get ticket(): TicketDetail {
    return this._ticket;
  }

  private _ticket!: TicketDetail;
  @Input() canEdit = true;
  @Output() commentAdded = new EventEmitter<TicketDetail>();
  @Output() timeEntryAdded = new EventEmitter<TicketDetail>();
  @Output() attachmentAdded = new EventEmitter<TicketDetail>();
  /**
   * Switch "Modo Edição". O estado real é do painel de informações (o pai repassa por aqui),
   * então o switch só *pede* a mudança — quem confirma é o pai. Assim, quando o usuário
   * cancela o descarte de alterações pendentes, o switch volta sozinho para o valor real.
   */
  @Input() set bulkEditMode(value: boolean) {
    this.bulkEditModeInput = value;
    this.bulkEditActive.set(value);
  }
  private bulkEditModeInput = false;
  @Output() bulkEditModeChange = new EventEmitter<boolean>();

  readonly bulkEditActive = signal(false);

  onSwitchToggled(requested: boolean): void {
    this.bulkEditActive.set(requested);
    this.bulkEditModeChange.emit(requested);
    // Se o pai não confirmou a mudança de forma síncrona (ex.: abriu a confirmação
    // de descarte), volta para o estado real — o valor definitivo chega por [bulkEditMode].
    setTimeout(() => this.bulkEditActive.set(this.bulkEditModeInput));
  }

  activeAction = signal<ActionType>('comment');
  commentText = signal('');
  isPublicComment = signal(false);
  sending = signal(false);
  uploadingAttachment = signal(false);
  isDragging = signal(false);
  pasteText = signal('');

  // TimeEntry properties
  showTimeEntryModal = signal(false);
  activeTimeEntry = signal<TicketLog | null>(null);
  preSelectedTaskId = signal<string | null>(null);

  // File name dialog
  showFileNameDialog = signal(false);
  fileName = signal('');
  pendingPasteText = signal('');
  private pendingImageBlob: File | null = null;

  private readonly MIME_TO_EXT: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp',
  };

  // Detectar apontamento ativo
  private findActiveTimeEntry(): TicketLog | null {
    const timeEntries = this.getTimeEntries();
    const user = utils.getUser();
    const bwUserCG = utils.getBwUserCompanyGroup(user);
    const correctUserId = bwUserCG?.user_id || user?.id;

    const activeEntry =
      timeEntries.find(
        entry =>
          !entry.end_date && (entry.user_id === correctUserId || entry.user?.id === correctUserId)
      ) || null;
    return activeEntry;
  }

  // Obter label do botão de apontamento
  getApontamentoButtonLabel(): string {
    const activeEntry = this.findActiveTimeEntry();
    if (activeEntry && activeEntry.user?.name) {
      return `Apontamento (${activeEntry.user.name})`;
    }
    return 'Apontamento';
  }

  // Obter severity do botão de apontamento
  getApontamentoButtonSeverity(): 'secondary' | 'warn' {
    const activeEntry = this.findActiveTimeEntry();
    return activeEntry ? 'warn' : 'secondary';
  }

  setAction(type: ActionType): void {
    this.activeAction.set(type);
  }

  onSendComment(): void {
    const text = this.commentText().trim();
    if (!text) return;

    const user = utils.getUser();
    if (!user) return;

    const bwUserCG = utils.getBwUserCompanyGroup(user);
    const correctUserId = bwUserCG?.user_id || user.id;

    this.sending.set(true);

    this.ticketService
      .addComment(this.ticket.id, {
        observation: text,
        user_id: correctUserId,
        user: { name: user.name ?? '' },
        pub: this.isPublicComment(),
      })
      .subscribe({
        next: updatedTicket => {
          this.commentText.set('');
          this.isPublicComment.set(false); // Reset checkbox
          this.sending.set(false);
          this.commentAdded.emit(updatedTicket);
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Comentário adicionado',
            life: 3000,
          });
        },
        error: () => {
          this.sending.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao adicionar comentário',
            life: 5000,
          });
        },
      });
  }

  onUploadAttachment(event: any, uploader: any): void {
    const file = event.files[0];
    if (!file || !this.ticket) return;

    this.uploadingAttachment.set(true);
    this.isDragging.set(false);
    const path = `chamados/${this.ticket.ticket_number_teorema}`;

    this.ticketService.uploadAttachment(this.ticket.id, file, path).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Anexo adicionado',
        });
        this.uploadingAttachment.set(false);
        uploader.clear();

        // Recarrega o ticket
        this.ticketService.getById(this.ticket.id).subscribe(updatedTicket => {
          this._ticket = updatedTicket;
          this.attachmentAdded.emit(updatedTicket);
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao enviar anexo',
        });
        this.uploadingAttachment.set(false);
        uploader.clear();
      },
    });
  }

  private generateDefaultFileName(extension: string): string {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    return `arquivo_${date}_${time}.${extension}`;
  }

  onPasteTextChange(text: string): void {
    this.pasteText.set(text);

    if (text.trim() && !this.uploadingAttachment()) {
      this.pendingPasteText.set(text);
      this.pendingImageBlob = null;
      this.fileName.set(this.generateDefaultFileName('txt'));
      this.showFileNameDialog.set(true);
    }
  }

  onPasteImage(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const blob = item.getAsFile();
        if (blob && this.ticket) {
          const ext = this.MIME_TO_EXT[blob.type] ?? 'png';
          const defaultName = this.generateDefaultFileName(ext);

          this.pendingImageBlob = new File([blob], defaultName, { type: blob.type });
          this.pendingPasteText.set('');
          this.fileName.set(defaultName);
          this.showFileNameDialog.set(true);
        }
        break;
      }
    }
  }

  onConfirmFileName(): void {
    const rawName = this.fileName().trim();
    if (!rawName) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'Por favor, digite um nome para o arquivo',
        life: 3000,
      });
      return;
    }

    this.showFileNameDialog.set(false);

    if (this.pendingImageBlob) {
      this.uploadPendingImage(rawName);
    } else {
      this.createFileFromPaste(rawName);
    }
  }

  onCancelFileName(): void {
    this.showFileNameDialog.set(false);
    this.pasteText.set('');
    this.pendingPasteText.set('');
    this.fileName.set('');
    this.pendingImageBlob = null;
  }

  private getExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  private ensureExtension(name: string, ext: string): string {
    const currentExt = this.getExtension(name);
    // Se o usuário já digitou uma extensão válida, respeitar
    if (currentExt && currentExt !== '') return name;
    return `${name}.${ext}`;
  }

  private uploadPendingImage(fileName: string): void {
    const blob = this.pendingImageBlob;
    if (!blob || !this.ticket) return;

    const ext = this.MIME_TO_EXT[blob.type] ?? 'png';
    const finalFileName = this.ensureExtension(fileName, ext);
    const file = new File([blob], finalFileName, { type: blob.type });
    const path = `chamados/${this.ticket.ticket_number_teorema}`;

    this.uploadingAttachment.set(true);
    this.pendingImageBlob = null;

    this.ticketService.uploadAttachment(this.ticket.id, file, path).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Imagem anexada com sucesso',
        });
        this.uploadingAttachment.set(false);
        this.pasteText.set('');
        this.ticketService.getById(this.ticket.id).subscribe(updatedTicket => {
          this._ticket = updatedTicket;
          this.attachmentAdded.emit(updatedTicket);
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao anexar imagem',
        });
        this.uploadingAttachment.set(false);
      },
    });
  }

  private createFileFromPaste(fileName: string): void {
    const text = this.pendingPasteText().trim();
    if (!text || !this.ticket) return;

    const finalFileName = this.ensureExtension(fileName, 'txt');
    const file = new File([new Blob([text], { type: 'text/plain' })], finalFileName, {
      type: 'text/plain',
    });
    const path = `chamados/${this.ticket.ticket_number_teorema}`;

    this.uploadingAttachment.set(true);

    this.ticketService.uploadAttachment(this.ticket.id, file, path).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Arquivo criado a partir do texto',
        });
        this.uploadingAttachment.set(false);
        this.pasteText.set('');
        this.ticketService.getById(this.ticket.id).subscribe(updatedTicket => {
          this._ticket = updatedTicket;
          this.attachmentAdded.emit(updatedTicket);
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao criar arquivo',
        });
        this.uploadingAttachment.set(false);
      },
    });
  }

  getTimeEntries(): TicketLog[] {
    if (!this.ticket?.ticket_logs) return [];

    return this.ticket.ticket_logs
      .filter(
        log =>
          // Aceitar tanto abreviações quanto nomes completos
          log.type === 'M' ||
          log.type === 'Manutenção' ||
          log.type === 'E' ||
          log.type === 'Erro' ||
          log.type === 'I' ||
          log.type === 'Inovação' ||
          log.type === 'A' ||
          log.type === 'Acompanhamento' ||
          log.type === 'Apontamento'
      )
      .sort((a, b) => {
        const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
        const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
        return dateB - dateA;
      });
  }

  openTimeEntryModal(taskId?: string): void {
    // Detectar apontamento ativo antes de abrir o modal
    const activeEntry = this.findActiveTimeEntry();
    this.activeTimeEntry.set(activeEntry);

    // Se foi passado taskId, usar esse
    // Se não foi passado, procurar apontamento ativo de outro usuário e pré-selecionar a tarefa dele
    if (taskId) {
      this.preSelectedTaskId.set(taskId);
    } else {
      // Procurar apontamento ativo de outro usuário
      const otherUserActiveEntry = this.findOtherUserActiveTimeEntry();
      if (otherUserActiveEntry && otherUserActiveEntry.task_id) {
        // Pré-selecionar a tarefa do apontamento ativo de outro usuário
        this.preSelectedTaskId.set(otherUserActiveEntry.task_id.toString());
      } else {
        this.preSelectedTaskId.set(null);
      }
    }

    this.showTimeEntryModal.set(true);
  }

  // Procurar apontamento ativo de outro usuário (não do usuário logado)
  private findOtherUserActiveTimeEntry(): TicketLog | null {
    const timeEntries = this.getTimeEntries();
    const user = utils.getUser();
    const bwUserCG = utils.getBwUserCompanyGroup(user);
    const correctUserId = bwUserCG?.user_id || user?.id;

    const otherUserEntry =
      timeEntries.find(
        entry =>
          !entry.end_date && entry.user_id !== correctUserId && entry.user?.id !== correctUserId
      ) || null;
    return otherUserEntry;
  }

  closeTimeEntryModal(): void {
    this.showTimeEntryModal.set(false);
  }

  onTimeEntryModalSaved(ticket: TicketDetail): void {
    // Atualização otimista imediata
    this._ticket = ticket;
    this.closeTimeEntryModal();

    // Recarregar ticket para garantir relacionamentos completos (ex.: task_id no log ativo)
    this.ticketService.getById(this.ticket.id).subscribe({
      next: (updatedTicket: TicketDetail) => {
        this._ticket = updatedTicket;
        this.timeEntryAdded.emit(updatedTicket);
        this.updateActiveTimeEntry();
      },
      error: () => {
        // Fallback para não perder atualização da UI caso o reload falhe
        this.timeEntryAdded.emit(ticket);
        this.updateActiveTimeEntry();
      },
    });
  }

  onStopTimeEntry(): void {
    // Limpar apontamento ativo imediatamente
    this.activeTimeEntry.set(null);

    // Recarregar ticket para obter dados atualizados do apontamento encerrado
    this.ticketService.getById(this.ticket.id).subscribe({
      next: (updatedTicket: TicketDetail) => {
        this.ticket = updatedTicket;
        this.timeEntryAdded.emit(updatedTicket); // Emitir ticket atualizado
      },
      error: (err: { message?: string; status?: number; error?: string }) => {
        console.error('Erro ao recarregar ticket após encerrar apontamento:', err);
      },
    });
  }

  private updateActiveTimeEntry() {
    const activeEntry = this.findActiveTimeEntry();
    this.activeTimeEntry.set(activeEntry);
  }

  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent): void {
    if (this.canEdit && event.dataTransfer?.types.includes('Files')) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging.set(true);
      // Ativar aba de anexo se estiver arrastando arquivo
      this.setAction('attachment');
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    if (this.canEdit && event.dataTransfer?.types.includes('Files')) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging.set(true);
    }
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    if (this.canEdit) {
      event.preventDefault();
      event.stopPropagation();

      // Verificar se realmente saiu do elemento (não entrou em um filho)
      const target = event.relatedTarget as HTMLElement;
      if (
        !target ||
        !event.currentTarget ||
        !(event.currentTarget as HTMLElement).contains(target)
      ) {
        this.isDragging.set(false);
      }
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    if (this.canEdit && event.dataTransfer?.types.includes('Files')) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging.set(false);
      // O p-fileUpload já lida com o drop do arquivo se estiver visível
    } else {
      this.isDragging.set(false);
    }
  }
}
